import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';

function formatTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

const ClassDetailsModal = ({ 
  isOpen, 
  onClose, 
  userId, 
  title = "Academic Program & Coursework Registry" 
}) => {
  const [classMappingData, setClassMappingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchClassMappingDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const subdomain = window.location.hostname.split('.')[0];
      
      const response = await api.post('/api/student/class-mapping', {
        userId,
        subdomain
      });

      setClassMappingData(response.data.data);
    } catch (err) {
      console.error('Error fetching class mapping details:', err);
      setError(err.response?.data?.message || 'Failed to sync academic records.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen && userId) {
      fetchClassMappingDetails();
    }
  }, [isOpen, fetchClassMappingDetails, userId]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const handleClose = () => {
    setClassMappingData(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const mapping = classMappingData?.classMapping;
  const showSemester = mapping?.isSemesterBased && mapping?.semesterNumber;
  const showTerm = !mapping?.isSemesterBased && mapping?.termName;
  const subjects = mapping?.subjects || [];
  const subjectsWithFaculty = subjects.filter(s => s.faculty).length;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-hidden font-sans">
      {/* Institutional Backdrop Dimmer */}
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity duration-200" onClick={handleClose} />

      {/* Main Structural Sheet Container */}
      <div className="relative bg-slate-50/90 w-full rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-5xl h-[94vh] sm:h-auto sm:max-h-[88vh] flex flex-col overflow-hidden border border-slate-200/80 animate-in slide-in-from-bottom sm:zoom-in-95 duration-200 backdrop-blur-md">
        
        {/* Header Block Section */}
        <div className="bg-gradient-to-r from-indigo-900 to-slate-900 px-6 py-4 flex items-center justify-between gap-4 shrink-0 shadow-md">
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-bold text-white tracking-tight flex items-center gap-2">
              <span className="p-1.5 bg-indigo-500/20 rounded-lg text-indigo-300 text-sm">📖</span>
              {title}
            </h2>
            {mapping?.className && (
              <p className="text-[11px] text-indigo-200/80 mt-0.5 font-medium tracking-wide">
                Enrolled Cohort: <span className="text-white font-semibold">{mapping.className}</span>
              </p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="text-indigo-200/70 hover:text-white p-1.5 hover:bg-white/10 rounded-xl transition-all shrink-0 border border-white/10"
            aria-label="Close panel"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Academic Session Sub-Bar & High-Density Stats Row */}
        <div className="bg-indigo-50 border-b border-indigo-100/80 px-6 py-2 shrink-0 text-[11px] text-indigo-950 font-medium tracking-wide flex flex-wrap items-center justify-between gap-y-2">
          {/* Metadata Parameters */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="flex items-center gap-1 bg-white/80 px-2 py-0.5 rounded border border-indigo-100 shadow-2xs">
              📅 Term: <strong className="text-indigo-900 font-bold">{classMappingData?.academicYear?.name || '--'}</strong>
            </span>
            {showSemester && (
              <span className="flex items-center gap-1 bg-white/80 px-2 py-0.5 rounded border border-indigo-100 shadow-2xs">
                🎯 Semester: <strong className="text-indigo-900 font-bold">{mapping.semesterNumber}</strong>
              </span>
            )}
            {showTerm && (
              <span className="flex items-center gap-1 bg-white/80 px-2 py-0.5 rounded border border-indigo-100 shadow-2xs">
                🏷️ Sequence: <strong className="text-indigo-900 font-bold">{mapping.termName}</strong>
              </span>
            )}
          </div>

          {/* Compact Integrated Metrics Counters */}
          {classMappingData && !loading && !error && (
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="text-slate-400 font-bold uppercase text-[9px] tracking-wider">Modules:</span>
                <span className="font-extrabold text-indigo-950 bg-indigo-200/50 px-2 py-0.5 rounded text-[11px]">{subjects.length} Units</span>
              </div>
              <div className="w-px h-3 bg-indigo-200/80" />
              <div className="flex items-center gap-1.5">
                <span className="text-slate-400 font-bold uppercase text-[9px] tracking-wider">Faculty:</span>
                <span className="font-extrabold text-emerald-950 bg-emerald-100 px-2 py-0.5 rounded text-[11px]">{subjectsWithFaculty} Allotted</span>
              </div>
            </div>
          )}
        </div>

        {/* Scrollable Document Body */}
        <div className="flex-1 p-6 overflow-y-auto space-y-5">
          
          {loading && (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-slate-200/60 shadow-sm">
              <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-3">Retrieving Registrar Records...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12 max-w-md mx-auto bg-white border border-rose-200 rounded-xl p-5 space-y-3 shadow-sm">
              <div className="w-10 h-10 bg-rose-50 rounded-full flex items-center justify-center mx-auto text-rose-500 font-bold">⚠️</div>
              <p className="text-xs font-medium text-slate-700">{error}</p>
              <div className="flex gap-2 justify-center pt-1">
                <button onClick={fetchClassMappingDetails} className="px-4 py-1.5 bg-indigo-600 text-white hover:bg-indigo-700 font-semibold text-xs rounded-lg transition-all shadow-sm">
                  Retry Sync
                </button>
                <button onClick={handleClose} className="px-4 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs font-semibold rounded-lg transition-colors">
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {classMappingData && !loading && !error && (
            <div className="space-y-4 animate-in fade-in-50 duration-150">
              
              <div className="border-b border-slate-200 pb-1.5">
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Curriculum Structure & Allotments</h3>
              </div>

              {subjects.length === 0 ? (
                <div className="text-center py-10 bg-white rounded-xl border border-dashed border-slate-300">
                  <p className="text-xs text-slate-400 italic">No operational coursework blocks assigned to this academic track.</p>
                </div>
              ) : (
                <div className="space-y-3.5">
                  {subjects.map((subject, index) => (
                    <div key={index} className="border border-slate-200/80 rounded-xl overflow-hidden bg-white shadow-2xs hover:border-indigo-200 hover:shadow-xs transition-all duration-150">
                      
                      {/* Course Name Header Ribbon */}
                      <div className="bg-slate-50/60 border-b border-slate-150 px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="min-w-0">
                          <h4 className="text-sm font-bold text-slate-900 leading-snug">
                            {subject.name || subject.subjectName || `Module Block ${index + 1}`}
                          </h4>
                          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] font-mono text-slate-500 font-medium mt-0.5">
                            {subject.code && subject.code !== 'N/A' && (
                              <span className="bg-slate-200/50 px-1 py-0.5 rounded text-slate-700">Code: {subject.code}</span>
                            )}
                            {subject.credits > 0 && (
                              <span className="bg-indigo-50 px-1.5 py-0.5 rounded text-indigo-700 font-semibold">Credits: {subject.credits}</span>
                            )}
                          </div>
                        </div>

                        {/* Curricular Tag Badges */}
                        <div className="flex flex-wrap gap-1 text-[9px] font-bold uppercase tracking-wider self-start sm:self-auto">
                          {subject.isElective && subject.electiveGroup ? (
                            <span className="px-2 py-0.5 bg-amber-50 border border-amber-200 rounded text-amber-800">
                              🌟 {subject.electiveGroup}
                            </span>
                          ) : subject.type && (
                            <span className="px-2 py-0.5 bg-blue-50 border border-blue-200 rounded text-blue-800">
                              {subject.type}
                            </span>
                          )}
                          {subject.labBatch && (
                            <span className="px-2 py-0.5 bg-purple-50 border border-purple-200 rounded text-purple-800">
                              Batch {subject.labBatch}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Outer Body Content Block */}
                      <div className="p-4 space-y-3">
                        {/* Description Block */}
                        {subject.description && (
                          <p className="text-xs text-slate-600 leading-relaxed bg-slate-50/40 p-2.5 rounded-lg border border-slate-200/60 font-medium">
                            {subject.description}
                          </p>
                        )}

                        {/* Faculty Profile Segment */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-0.5">
                          {subject.faculty ? (
                            <>
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-[11px] shadow-2xs shrink-0">
                                  {subject.faculty.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                  <div className="text-xs font-bold text-slate-800 truncate">
                                    {subject.faculty.name}
                                  </div>
                                  <div className="text-[11px] font-medium text-slate-500 truncate mt-0.5">
                                    ✉️ {subject.faculty.email || 'No assigned address'}
                                  </div>
                                </div>
                              </div>
                              {subject.faculty.phoneNumber && (
                                <div className="text-[11px] font-mono font-bold text-slate-700 bg-white border border-slate-200 px-2 py-0.5 rounded-md self-start sm:self-auto shadow-3xs">
                                  📞 {subject.faculty.phoneNumber}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-[11px] font-medium text-amber-700 bg-amber-50/60 border border-amber-200/40 px-2 py-1 rounded-lg italic">
                              ⏳ Instructional allotment pending assignment
                            </div>
                          )}
                        </div>
                      </div>

                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom Sticky Action Footer */}
        <div className="bg-white border-t border-slate-200 px-6 py-3.5 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0 shadow-[0_-4px_12px_rgba(0,0,0,0.01)]">
          <div className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest text-center sm:text-left">
            {mapping?.updatedAt ? (
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                Ledger Rested: {new Date(mapping.updatedAt).toLocaleDateString()} @ {formatTime(mapping.updatedAt)}
              </span>
            ) : (
              <span>Verified Institutional Record Ledger</span>
            )}
          </div>
          
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={fetchClassMappingDetails}
              className="flex-1 sm:flex-initial px-3.5 py-1.5 border border-slate-300 hover:border-slate-400 bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs rounded-xl transition-all"
            >
              Sync Records
            </button>
            <button
              onClick={handleClose}
              className="flex-1 sm:flex-initial px-5 py-1.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-all"
            >
              Close Ledger
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ClassDetailsModal;